'use client';

import React, { useEffect, useState, useRef } from 'react';
import { 
  Bell, Loader2, 
  AlertTriangle, CreditCard, Gift, ShieldAlert 
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useMainModal } from '@/components/MainModalProvider';

interface Notification {
  id: number;
  userId: number;
  type: string; 
  title: string;
  content: string; 
  fullContent?: string; 
  actionUrl?: string; 
  isRead: boolean;
  createdAt: string;
}

interface InboxDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onMarkAsReadSuccess?: () => void;
  inline?: boolean;
  triggerRef?: { current: HTMLButtonElement | null };
}

const LOCAL_API_URL = process.env.NEXT_PUBLIC_LOCAL_API;

export default function InboxDropdown({ 
  isOpen, onClose, user, onMarkAsReadSuccess, inline = false, triggerRef 
}: InboxDropdownProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { openModal } = useMainModal();

  // Закрытие самого дропдауна
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !triggerRef?.current?.contains(target)
      ) {
        onClose();
      }
    };
    if (isOpen && !inline) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, inline, onClose, triggerRef]);

  useEffect(() => {
    if (isOpen && user?.oauthId) fetchNotifications();
  }, [isOpen, user]);

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${LOCAL_API_URL}/api/inbox/notifications/${user.oauthId}`, { credentials: 'include' });
      if (res.ok) setNotifications(await res.json());
    } catch (e) {
      console.error("Ошибка загрузки уведомлений:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const markAllAsRead = async () => {
    if (isMarking || notifications.every(n => n.isRead)) return;
    setIsMarking(true);
    try {
      const res = await fetch(`${LOCAL_API_URL}/api/inbox/notifications/${user.oauthId}/read`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        if (onMarkAsReadSuccess) onMarkAsReadSuccess();
      }
    } catch (e) {} finally {
      setIsMarking(false);
    }
  };

  // Метод только для того, чтобы отметить прочитанным при клике
  const markAsReadOptimistic = async (notif: Notification) => {
    if (notif.isRead) return;
    
    // Оптимистичное обновление интерфейса
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
    try {
      await fetch(`${LOCAL_API_URL}/api/inbox/notifications/${user.oauthId}/${notif.id}/read`, { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.error(e);
      // Возвращаем статус если произошла ошибка
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: false } : n));
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    // Отмечаем прочитанным при открытии
    markAsReadOptimistic(notif);

    onClose(); 

    if (notif.actionUrl) {
      router.push(notif.actionUrl);
      return;
    }

    let modalType: 'info' | 'success' | 'error' = 'info';
    if (notif.type === 'WARN' || notif.type === 'SYSTEM') modalType = 'error';
    if (notif.type === 'PAYMENT' || notif.type === 'GIFT') modalType = 'success';

    openModal({
      title: notif.title,
      message: notif.fullContent || notif.content, 
      type: modalType,
      confirmText: 'Закрыть',
      messageColor: 'text-zinc-300' 
    });
  };

  const getIconStylesForType = (type: string, isRead: boolean) => {
    if (isRead) {
      return { wrapper: 'bg-[#1c202d]', icon: 'text-[#787f8f]' };
    }
    switch (type) {
      case 'PAYMENT': return { wrapper: 'bg-emerald-500/10', icon: 'text-emerald-400' };
      case 'WARN':    return { wrapper: 'bg-amber-500/10',   icon: 'text-amber-400' };
      case 'SYSTEM':  return { wrapper: 'bg-red-500/10',     icon: 'text-red-400' };
      case 'GIFT':    return { wrapper: 'bg-purple-500/10',  icon: 'text-purple-400' };
      default:        return { wrapper: 'bg-indigo-500/10',  icon: 'text-indigo-400' };
    }
  };

  const renderIcon = (type: string, isRead: boolean) => {
    const { icon: colorClass } = getIconStylesForType(type, isRead);
    switch (type) {
      case 'PAYMENT': return <CreditCard size={18} className={colorClass} />;
      case 'WARN':    return <AlertTriangle size={18} className={colorClass} />;
      case 'SYSTEM':  return <ShieldAlert size={18} className={colorClass} />;
      case 'GIFT':    return <Gift size={18} className={colorClass} />;
      default:        return <Bell size={18} className={colorClass} />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return 'Только что';
    if (diff < 3600) return `${Math.floor(diff / 60)} мин.`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ч.`;
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  const filteredNotifs = filter === 'all' ? notifications : notifications.filter(n => !n.isRead);
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const content = (
    <>
      <div className={`flex items-center justify-between shrink-0 mb-5 ${inline ? '' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center text-white">
            <Bell size={24} />
          </div>
          <div>
            <h3 className="text-white font-semibold text-[17px] leading-tight">
              Уведомления ({unreadCount})
            </h3>
          </div>
        </div>
        
        {unreadCount > 0 && (
          <button 
            onClick={markAllAsRead}
            disabled={isMarking}
            className="flex items-center gap-1.5 text-[13px] font-medium text-[#8b919e] hover:text-white transition-colors cursor-pointer group px-3 py-1.5 rounded-lg hover:bg-white/5 active:scale-[0.98] outline-none"
          >
            {isMarking && <Loader2 size={14} className="animate-spin" />}
            Прочитать всё
          </button>
        )}
      </div>

      <div className="flex bg-[#11141d] rounded-xl p-1 mb-4 border border-[#232734] shrink-0">
        <button
          onClick={() => setFilter('all')}
          className={`flex-1 flex justify-center items-center gap-2 py-2 rounded-[9px] text-[14px] transition-all duration-300 ease-out active:scale-[0.98] outline-none cursor-pointer ${
            filter === 'all' 
              ? 'bg-[#1c202d] text-white font-semibold shadow-sm scale-[1.02]' 
              : 'text-[#8b919e] hover:text-white font-medium'
          }`}
        >
          Все
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`flex-1 flex justify-center items-center gap-2 py-2 rounded-[9px] text-[14px] transition-all duration-300 ease-out active:scale-[0.98] outline-none cursor-pointer ${
            filter === 'unread' 
              ? 'bg-[#1c202d] text-white font-semibold shadow-sm scale-[1.02]' 
              : 'text-[#8b919e] hover:text-white font-medium'
          }`}
        >
          <span className="relative">
            Новые
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-2.5 w-1.5 h-1.5 rounded-full bg-blue-500"></span>
            )}
          </span>
        </button>
      </div>

      <div 
        data-lenis-prevent
        className="flex-1 overflow-y-auto pr-1 flex flex-col gap-2.5 min-h-[220px] max-h-[380px] overscroll-contain
        [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-white/20"
      >
        {isLoading ? (
          <div className="m-auto flex flex-col items-center justify-center text-zinc-500 gap-3 py-10">
            <Loader2 size={28} className="animate-spin text-white/20" />
            <span className="text-[14px] font-medium text-[#8b919e]">Загрузка данных...</span>
          </div>
        ) : filteredNotifs.length === 0 ? (
          <div className="m-auto flex flex-col items-center justify-center text-center px-6 py-10">
            <div className="w-[100px] h-[100px] mb-4 overflow-hidden rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.4)] border border-[#232734]/70">
              <img 
                src="https://rplands.royolands.com/landing2.0/no_notifications.avif" 
                alt="Нет уведомлений" 
                className="w-full h-full object-cover select-none pointer-events-none"
                draggable={false}
              />
            </div>
            <span className="text-[15px] font-semibold text-zinc-300">
              {filter === 'unread' ? 'Нет новых уведомлений' : 'Уведомлений пока нет'}
            </span>
          </div>
        ) : (
          filteredNotifs.map((notif) => {
            const { wrapper } = getIconStylesForType(notif.type, notif.isRead);
            return (
              <button
                key={notif.id} 
                onClick={() => handleNotificationClick(notif)}
                className={`group w-full text-left relative flex items-start gap-3 py-3 px-4 rounded-[16px] outline-none cursor-pointer transform-gpu transition-all duration-300 ease-out active:scale-[0.98]
                ${notif.isRead 
                  ? 'hover:bg-white/[0.04]' 
                  : 'bg-white/[0.03] hover:bg-white/[0.06] border-l-[3px] border-l-blue-500/80 shadow-[0_4px_20px_rgba(0,0,0,0.2)]'
                }`}
              >
                {/* Иконка */}
                <div className={`w-[42px] h-[42px] rounded-[12px] flex shrink-0 items-center justify-center ${wrapper} shadow-sm`}>
                  {renderIcon(notif.type, notif.isRead)}
                </div>

                {/* Основной контент */}
                <div className="flex-1 min-w-0 pr-1">
                  <div className="flex justify-between items-start mb-1 gap-2">
                    <span className={`text-[15px] font-semibold leading-tight line-clamp-1 group-hover:text-zinc-200 transition-colors ${notif.isRead ? 'text-zinc-300' : 'text-white'}`}>
                      {notif.title}
                    </span>
                    <span className={`text-[12px] whitespace-nowrap mt-[1px] shrink-0 font-medium ${notif.isRead ? 'text-[#5a6072]' : 'text-blue-400/80'}`}>
                      {formatTime(notif.createdAt)}
                    </span>
                  </div>
                  
                  <p className="text-[13px] text-[#8b919e] leading-snug line-clamp-2 pr-4">
                    {notif.content}
                  </p>
                </div>
              </button>
            )
          })
        )}
      </div>
    </>
  );

  if (inline) {
    return (
      <div 
        ref={dropdownRef}
        className={`transition-all duration-300 ease-in-out origin-top ${
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.98] pointer-events-none'
        }`}
      >
        <div className="bg-[#0e1017] rounded-[20px] p-5">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={dropdownRef}
      className={`absolute top-full right-0 z-50 mt-3 w-[480px] transform-gpu transition-all duration-150 ease-out origin-top-right ${
        isOpen
          ? 'opacity-100 translate-y-0 pointer-events-auto scale-100'
          : 'opacity-0 -translate-y-1 pointer-events-none scale-[0.98]'
      }`}
    >
      <div className="bg-[#0e1017] border border-[#232734] rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.7)] p-6 text-white flex flex-col max-h-[580px]">
        {content}
      </div>
    </div>
  );
}